import { useMemo } from 'react';
import { PERIOD_RESOLUTION_ENUM } from '~/modules/resourcing/common/enums';
import { OVERVIEW_UNITS_ENUM } from '~/modules/resource-management/common/enums';
import { mapRepliconDateToUtcObject } from '~/modules/common/dates/convert';
import { getFormattedDateForScale } from '~/modules/resource-management/ResourcingOverview/util';
import { useResourceAllocationSeriesData } from '~/modules/resourcing/common/hooks';
import {
  convertResourceAllocationSeriesDataToFTEUnits,
  convertResourceAllocationSeriesDataToPercentageUnits
} from '~/modules/resource-management/ResourcingOverview/components/ResourceAllocationChart/hooks/fte-conversion';

const DECIMAL_PLACES = 2;

const unitConverters = {
  [OVERVIEW_UNITS_ENUM.FTE]: convertResourceAllocationSeriesDataToFTEUnits,
  [OVERVIEW_UNITS_ENUM.PERCENTAGE]: data =>
    convertResourceAllocationSeriesDataToPercentageUnits({
      resourceAllocationSeriesData: data
    }),
  [OVERVIEW_UNITS_ENUM.HOURS]: data => data
};

const formatNumber = (value, decimals = DECIMAL_PLACES) =>
  Number((value || 0).toFixed(decimals));

const mapToChartDataPoint = (allocation, periodScale) => ({
  period: getFormattedDateForScale(
    periodScale,
    mapRepliconDateToUtcObject(allocation.dateRange.startDate)
  ),
  startDate: mapRepliconDateToUtcObject(allocation.dateRange.startDate),
  bench: formatNumber(allocation.totalAvailable),
  overAllocation: formatNumber(allocation.overAllocated)
});

const calculateMaxValue = dataPoints => {
  const maxPerPeriod = dataPoints.map(item =>
    Math.max(item.bench, item.overAllocation, 0)
  );

  return Math.max(...maxPerPeriod);
};

export const useBenchOverAllocationDataPoints = ({
  overViewUnit,
  dateRange,
  periodScale,
  filter = {}
}) => {
  const {
    loading,
    resourceAllocationSeriesData
  } = useResourceAllocationSeriesData({
    dateRange,
    periodResolution: PERIOD_RESOLUTION_ENUM[periodScale],
    filter
  });

  return useMemo(() => {
    if (loading || !resourceAllocationSeriesData) {
      return {
        loading,
        dataPoints: [],
        maxValue: 0
      };
    }

    const convertToUnit = unitConverters[overViewUnit];
    const dataInDesiredUnits = convertToUnit(resourceAllocationSeriesData);

    const dataPoints = (dataInDesiredUnits || []).map(allocation =>
      mapToChartDataPoint(allocation, periodScale)
    );

    return {
      loading,
      dataPoints,
      maxValue: calculateMaxValue(dataPoints)
    };
  }, [loading, resourceAllocationSeriesData, overViewUnit, periodScale]);
};

export default useBenchOverAllocationDataPoints;
